import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * Dropdown-фильтр для шапки таблицы.
 *   <HeaderSelect
 *     label="Тип"                              // ВСЕГДА показывается в шапке (не меняется)
 *     value="planned" | ['1','5']               // single-select: string; multi: array
 *     options={[{value, label}]}
 *     onChange={(val) => ...}                   // single: новое или null; multi: массив
 *     multi={true}                              // включить multi-select (apply on close)
 *   />
 *
 * Триггер всегда показывает label (без подмены на selected) — ширина шапки не прыгает.
 * Активный фильтр меняет только цвет/жирность.
 * Multi: галки накапливаются локально, apply при закрытии.
 */
function HeaderSelect({ label, value, options, onChange, multi = false }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const [tempValue, setTempValue] = useState(multi ? (value || []) : value);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => { setTempValue(multi ? (value || []) : value); }, [value, open, multi]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) {
        if (multi) {
          const cur = value || [];
          const same = tempValue.length === cur.length && tempValue.every(v => cur.includes(v));
          if (!same) onChange(tempValue);
        }
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open, tempValue, value, multi, onChange]);

  // Escape закрывает (UX-аудит 25.10); перехват раньше Modal — окно под ним остаётся
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open]);

  const toggle = () => {
    if (!open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.left });
    }
    setOpen(o => !o);
  };

  const pickSingle = (val) => {
    onChange(val);
    setOpen(false);
  };
  const flipMulti = (val) => {
    setTempValue(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);
  };
  const clearAll = () => {
    if (multi) { setTempValue([]); onChange([]); setOpen(false); }
    else { onChange(null); setOpen(false); }
  };

  const isActive = multi ? (value && value.length > 0) : !!value;
  const count = multi ? (value || []).length : (isActive ? 1 : 0);
  const labelText = isActive ? `${label} (${count})` : label;

  return (
    <>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <button
          ref={triggerRef}
          type="button"
          onClick={toggle}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: 0, border: 'none', background: 'transparent',
            color: isActive ? '#1C3D2E' : 'inherit',
            fontWeight: isActive ? 700 : 'inherit',
            fontSize: 'inherit', fontFamily: 'inherit',
            letterSpacing: 'inherit', textTransform: 'inherit',
            cursor: 'pointer',
          }}
        >
          <span>{labelText}</span>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
            style={{ transition: 'transform 0.15s', transform: open ? 'rotate(180deg)' : 'none' }}
          >
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </button>
        {isActive && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); clearAll(); }}
            title="Сбросить фильтр"
            style={{
              width: 18, height: 18, border: 'none', background: '#1C3D2E', borderRadius: '50%',
              cursor: 'pointer', color: '#fff', fontSize: 12, lineHeight: 1,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}
          >×</button>
        )}
      </span>
      {open && createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: 'fixed', top: pos.top, left: pos.left, zIndex: 1500,
            background: '#fff', border: '1px solid #D8DDDA', '--ch': '8px', '--stroke': '#D8DDDA',
            boxShadow: '0 8px 24px rgba(28,61,46,0.12)',
            minWidth: 200, maxWidth: 320, maxHeight: 400, overflowY: 'auto',
            padding: 4,
            textTransform: 'none', letterSpacing: 0,
          }}
        >
          {options.length === 0 ? (
            <div style={{ padding: 12, color: '#999', fontSize: 13, textAlign: 'center' }}>Нет опций</div>
          ) : options.map(opt => {
            const checked = multi
              ? (tempValue || []).map(String).includes(String(opt.value))
              : String(opt.value) === String(value);
            const handleClick = () => multi ? flipMulti(opt.value) : pickSingle(opt.value);
            return (
              <button key={opt.value} onClick={handleClick}
                style={{
                  width: '100%', textAlign: 'left',
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 10px', '--ch': '5px',
                  border: 'none', background: checked ? '#DFF2E8' : 'transparent',
                  fontSize: 13, color: checked ? '#1C3D2E' : '#1C3D2E',
                  fontWeight: checked ? 600 : 500,
                  cursor: 'pointer',
                }}
                onMouseEnter={e => { if (!checked) e.currentTarget.style.background = '#F9F9F6'; }}
                onMouseLeave={e => { if (!checked) e.currentTarget.style.background = 'transparent'; }}
              >
                {multi ? (
                  <input
                    type="checkbox"
                    checked={checked}
                    readOnly
                    style={{ width: 16, height: 16, accentColor: '#1C3D2E', cursor: 'pointer', flexShrink: 0, pointerEvents: 'none' }}
                  />
                ) : (
                  // место под галочку — для выравнивания single-варианта
                  <span style={{ width: 14, display: 'inline-flex', justifyContent: 'center' }}>
                    {checked && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    )}
                  </span>
                )}
                <span style={{ flex: 1 }}>{opt.label}</span>
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </>
  );
}

export default HeaderSelect;
